import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { useQueryClient } from '@tanstack/react-query'
import { isEqual } from 'lodash'
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import { useFeatureFlags } from '@node-space/hooks'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { VerificationTrackingLabels } from 'constants/verification'
import { BaseErrorResponse } from 'types/errors'
import {
  BusinessDocumentsPayload,
  CountList,
  DocumentCategories,
  DocumentNameType,
  Documents,
  DocumentUpload,
  PartialCategoryList,
  PartialUploadMessageList,
  PersonDocuments,
  RequiredDocument,
  RequiredDocumentsDetails,
  UploadedFile,
} from 'types/verification'
import {
  getDocumentInfo,
  getDocumentUploadPayload,
  getUploadMessages,
  isOwner,
} from 'utils/onboarding'
import { useVerificationContext } from './context/useVerificationContext'
import { useBusinessDocumentsMutation } from './mutations/useBusinessDocumentsMutation'
import { useAccountRepresentativeQuery } from './queries/useAccountRepresentativeQuery'
import { useRequiredDocumentsQuery } from './queries/useRequiredDocsQuery'
import { MappedErrorMessageState, useMappedErrorMessage } from './useMappedErrorMessage'

type UploadDocumentsHelperData = {
  data: {
    uploadMessages: PartialUploadMessageList
    documents: Documents
    uploadedFiles: PartialCategoryList
    uploadedFilesCount: CountList
    requiredFilesCount: CountList
    hasUploaded: boolean
    requiredDocuments: RequiredDocumentsDetails
    isLoading: boolean
    isFetchingRequiredDocuments: boolean
    isGetError: boolean
    requestError: MappedErrorMessageState
    isLoadingSubmitRequest: boolean
  }
  submitData: () => void
  navigateNextRoute: () => void
}

export const useUploadDocumentsHelper = (): UploadDocumentsHelperData => {
  const { t } = useTranslation()

  const { enableIndividualFileUpload, enableQueuedFileUpload } = useFeatureFlags()
  const queryClient = useQueryClient()

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { setUnsavedChanges, submitStep } = useVerificationContext()

  const initialUploadedFiles: PartialCategoryList = {
    account: {
      certificateOfIncorporation: {},
      proofOfAddress: {},
      shareholderCertificate: {},
      shareholderStructureChart: {},
      operatingLicense: {},
      memorandumArticlesOfAssociation: {},
      registerOfDirectors: {},
      amlPolicy: {},
    },
    corporateOwners: {},
    individualOwners: {},
    directors: {},
    accountRepresentative: {
      proofOfId: {},
      proofOfAddress: {},
    },
    finance: {
      sourceOfFunds: {},
    },
  }

  const initialFilesCount: CountList = {
    account: 0,
    corporateOwners: 0,
    individualOwners: 0,
    directors: 0,
    accountRepresentative: 0,
    finance: 0,
  }

  const [uploadedFiles, setUploadedFiles] = useState<PartialCategoryList>(initialUploadedFiles)
  const [uploadedFilesCount, setUploadedFilesCount] = useState<CountList>(initialFilesCount)
  const [requiredFilesCount, setRequiredFilesCount] = useState<CountList>(initialFilesCount)
  const [hasCountedRequired, setHasCountedRequired] = useState(false)
  const [hasUploaded, setHasUploaded] = useState(false)

  const {
    data: requiredDocuments,
    isLoading: isLoadingRequiredDocuments,
    isFetching: isFetchingRequiredDocuments,
    isError: isRequiredDocumentsGetError,
  } = useRequiredDocumentsQuery()

  const {
    data: accountRepresentative,
    isLoading: isLoadingAccountRepresentative,
    isError: isAccountRepresentativeGetError,
  } = useAccountRepresentativeQuery()

  const { mutate: postBusinessDocuments, isPending: isLoadingSubmitRequest } =
    useBusinessDocumentsMutation(!!enableQueuedFileUpload)

  const isLoading = useMemo(
    () => isLoadingRequiredDocuments || isLoadingAccountRepresentative,
    [isLoadingRequiredDocuments, isLoadingAccountRepresentative]
  )

  const isGetError = useMemo(
    () =>
      isRequiredDocumentsGetError || isAccountRepresentativeGetError || isEmpty(requiredDocuments),
    [isRequiredDocumentsGetError, isAccountRepresentativeGetError, requiredDocuments]
  )

  useEffect(() => {
    setUnsavedChanges(hasUploaded)
  }, [hasUploaded])

  const navigateNextRoute = () => {
    submitStep(VerificationTrackingLabels.UPLOAD_DOCUMENTS)
  }

  const submitData = () => {
    resetRequestError()
    const payload = getPayloadData()

    postBusinessDocuments(payload, {
      onSuccess: async () => {
        if (!enableIndividualFileUpload) {
          navigateNextRoute()
        }

        if (enableIndividualFileUpload) {
          await queryClient.invalidateQueries({ queryKey: reactQueryKeys.requiredDocuments() })
          setUnsavedChanges(false)
          setUploadedFiles(initialUploadedFiles)
        }
      },
      onError: (error: BaseErrorResponse) => {
        const sentryTraceId = logSentryErrorAndGetTraceId('postUploadDocuments', error)
        setRequestError({
          errorCode: error?.status,
          sentryTraceId,
          show: true,
        })
        setUploadedFiles(initialUploadedFiles)
      },
    })
  }

  const updateUploadedFiles = (
    file: DocumentUpload,
    category: DocumentCategories,
    fileName: DocumentNameType,
    isOwner?: boolean,
    owner?: PersonDocuments
  ) => {
    if (!file) return

    const uploadedFilesCopy: PartialCategoryList = { ...uploadedFiles }

    if (isOwner) {
      uploadedFilesCopy[category][`${fileName}${owner?.id}`] = {
        name: fileName,
        ownerId: owner?.id,
        file: file,
      }
    } else {
      uploadedFilesCopy[category][fileName] = {
        name: fileName,
        file: file,
      }
    }

    setHasUploaded(true)
    setUploadedFiles(uploadedFilesCopy)
  }

  const documents = useMemo(
    () =>
      accountRepresentative &&
      getDocumentInfo(uploadedFiles, updateUploadedFiles, accountRepresentative, t),
    [uploadedFiles, updateUploadedFiles, accountRepresentative]
  )

  const uploadMessages = useMemo(
    () => accountRepresentative && getUploadMessages(accountRepresentative, t),
    [accountRepresentative]
  )

  const getPayloadData = (): BusinessDocumentsPayload => {
    const payload: BusinessDocumentsPayload = {
      accountDocuments: [],
      representativeDocuments: [],
      corporateOwnersDocuments: {},
      directorsDocuments: {},
      financeDocuments: [],
      individualOwnersDocuments: {},
    }

    Object.keys(uploadedFiles)?.map((categoryKey: DocumentCategories) => {
      const category = uploadedFiles?.[categoryKey]

      const payloadKey =
        categoryKey === 'accountRepresentative'
          ? 'representativeDocuments'
          : `${categoryKey}Documents`

      if (isOwner(categoryKey)) {
        Object.keys(category)?.map(document => {
          const upload: UploadedFile = category?.[document]

          if (!isEmpty(upload?.file)) {
            if (!payload[payloadKey][upload?.ownerId]) {
              payload[payloadKey][upload?.ownerId] = []
            }

            payload?.[payloadKey]?.[upload?.ownerId]?.push(
              getDocumentUploadPayload(upload?.name, upload?.file?.buffer as string)
            )
          }
        })
      } else {
        Object.keys(category)?.map((fileName: DocumentNameType) => {
          const upload = category?.[fileName]

          if (!isEmpty(upload?.file)) {
            payload?.[payloadKey].push(
              getDocumentUploadPayload(upload?.name, upload?.file?.buffer as string)
            )
          }
        })
      }
    })

    return payload
  }

  const getUploadedFilesCount = useCallback(() => {
    const uploadedFilesCountCopy: CountList = cloneDeep(initialFilesCount)
    const requiredFilesCountCopy: CountList = cloneDeep(initialFilesCount)

    Object.keys(omit(requiredDocuments, 'id'))?.map((categoryKey: DocumentCategories) => {
      const category = requiredDocuments?.[categoryKey]

      if (isOwner(categoryKey)) {
        category?.map(owner => {
          owner?.documents?.map((document: RequiredDocument) => {
            if (document?.filename) {
              uploadedFilesCountCopy[categoryKey] += 1
            }
            requiredFilesCountCopy[categoryKey] += 1
          })
        })
      } else {
        category?.map(document => {
          if (document?.filename) {
            uploadedFilesCountCopy[categoryKey] += 1
          }
          requiredFilesCountCopy[categoryKey] += 1
        })
      }
    })

    Object.keys(uploadedFiles)?.map((categoryKey: DocumentCategories) => {
      const category = uploadedFiles?.[categoryKey]

      Object.keys(category)?.map((document: DocumentNameType) => {
        const upload = category?.[document]

        const file = upload?.file
        if (!isEmpty(file)) {
          uploadedFilesCountCopy[categoryKey] += 1
        }
      })
    })

    setUploadedFilesCount(uploadedFilesCountCopy)
    setRequiredFilesCount(requiredFilesCountCopy)
    if (!hasCountedRequired) {
      setHasCountedRequired(true)
    }
  }, [uploadedFiles, requiredDocuments])

  // Initial load count
  useEffect(() => {
    if (!requiredDocuments || hasCountedRequired) {
      return
    }

    getUploadedFilesCount()
  }, [requiredDocuments, hasCountedRequired])

  // FF disabled. Bulk upload
  useEffect(() => {
    if (!requiredDocuments || !hasCountedRequired || enableIndividualFileUpload) {
      return
    }

    getUploadedFilesCount()
  }, [requiredDocuments, uploadedFiles, hasCountedRequired])

  // FF enabled. Individual upload
  useEffect(() => {
    if (!requiredDocuments || !hasCountedRequired || !enableIndividualFileUpload) {
      return
    }

    if (isEqual(uploadedFiles, initialUploadedFiles)) {
      return
    }

    if (hasUploaded) {
      getUploadedFilesCount()
      submitData()
    }
  }, [uploadedFiles, hasCountedRequired])

  return {
    data: {
      uploadMessages,
      documents,
      uploadedFiles,
      uploadedFilesCount,
      requiredFilesCount,
      hasUploaded,
      requiredDocuments,
      isLoading,
      isFetchingRequiredDocuments,
      isGetError,
      requestError,
      isLoadingSubmitRequest,
    },
    submitData,
    navigateNextRoute,
  }
}
